import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type NotificationItem = Awaited<ReturnType<typeof trpc.notifications.list.query>>["items"][number];

// The user-scoped live channel does not exist (live events are space-scoped),
// so the unread badge polls and refreshes on window focus.
export function useNotifications(options: { listEnabled: boolean }) {
  const trpcClient = useTRPC();
  const queryClient = useQueryClient();
  const unreadQuery = useQuery(
    trpcClient.notifications.unreadCount.queryOptions(undefined, {
      refetchInterval: 30_000,
      refetchOnWindowFocus: true,
    }),
  );
  const listQuery = useQuery(
    trpcClient.notifications.list.queryOptions({}, { enabled: options.listEnabled, placeholderData: (previous) => previous }),
  );
  const markReadMutation = useMutation(trpcClient.notifications.markRead.mutationOptions());
  const markAllReadMutation = useMutation(trpcClient.notifications.markAllRead.mutationOptions());

  async function reload() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpcClient.notifications.list.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpcClient.notifications.unreadCount.queryKey() }),
    ]);
  }

  async function markRead(notificationIds: string[]) {
    if (notificationIds.length === 0) return;
    await markReadMutation.mutateAsync({ notificationIds });
    await reload();
  }

  async function markAllRead() {
    await markAllReadMutation.mutateAsync();
    await reload();
  }

  return {
    items: listQuery.data?.items ?? [],
    isLoading: options.listEnabled && listQuery.isLoading,
    markAllRead,
    markRead,
    reload,
    unreadCount: unreadQuery.data ?? 0,
  };
}
